// /mnt/data/Welcome.js
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  Stack,
  Divider,
  Card,
  CardContent,
  // NEW:
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import Chart from 'chart.js/auto';
import { lightTheme, darkTheme } from '../../themes/themes-welcome';
import './Welcome.css';
import PartnersMarquee from './components/PartnersMarquee';
import FeatureGrid from './components/FeatureGrid';
import TrendingCoinsTable from './components/TrendingCoinsTable';
import AiTradeCTA from './components/AiTradeCTA';
import AppDownload from './components/AppDownload';
import mobileImage from './components/mobileApp.png';
import SocialLinks from './components/SocialLinks';
import TrustAndSteps from './components/TrustAndSteps';
import Footer from './components/Footer';
import ButtonBase from '@mui/material/ButtonBase';
import platformLogo from '../../Components/NavComponents/logo.png';

// SEO hooks
import { useSEO, useJsonLD } from './SEO';

function RevealOnScroll({ children, sx = {}, threshold = 0.15 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.unobserve(el);
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px)' : 'translateY(24px)',
        transition: 'opacity 700ms ease, transform 700ms ease',
        willChange: 'opacity, transform',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const styles = `
  .text-style {
    position: relative;
    display: inline-block;
    font-weight: 700;
    letter-spacing: 0.5px;
    font-size: 32px;
    color: #F3BA2F;
  }
  .text-style > span.hollow {
    -webkit-text-fill-color: transparent;
    text-fill-color: transparent;
    -webkit-text-stroke: 1.5px #F3BA2F;
    text-stroke: 1.5px #F3BA2F;
    font-size: 36px;
  }
  .text-style > span.solid {
    -webkit-text-fill-color: White;
    text-fill-color: #F3BA2F;
  }
`;

// 🔗 رابط تحميل الـ APK من الـ VPS (مكان واحد فقط تغيّره عند تحديث النسخة)
const APK_DOWNLOAD_URL = 'https://1cryptox.it.com/apk/1cryptox-v1.0.0.apk';

const Welcome = () => {
  const isLightTheme = useSelector((state) => state.theme.light);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [prices, setPrices] = useState({
    BTCUSDT: '109057.00',
    ETHUSDT: '3214.56',
    XRPUSDT: '5.20',
  });
  const [chartInstance, setChartInstance] = useState(null);
  const [profitChartInstance, setProfitChartInstance] = useState(null);
  const [profitData, setProfitData] = useState({
    total: 1250.3,
    weekly: [100, 150, 200, 175, 225, 200, 200],
  });
  const chartRef = useRef(null);

  // SEO
  useSEO({
    title: '1CryptoX | Secure Crypto Trading, AI Trading & Low Fees',
    description:
      'Trade on 1CryptoX with real-time markets, AI-powered trading tools, and low fees. Seamless onboarding, pro charts, and 24/7 support.',
    keywords: [
      'crypto exchange',
      'bitcoin trading',
      'ethereum',
      'ai trading',
      'low fees',
      'binance style',
      'spot trading',
      'futures',
      '1cryptox',
      'secure platform',
      'cryptocurrency',
      'btc/usdt',
      'web3',
      'crypto app download',
    ],
    canonical: 'https://1cryptox.com/',
    og: {
      title: '1CryptoX — Trade Smarter',
      description:
        'AI-powered crypto trading with pro charts, low fees, and 24/7 support.',
      url: 'https://1cryptox.com/',
      image: 'https://1cryptox.com/og-cover.jpg',
      siteName: '1CryptoX',
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title: '1CryptoX — Trade Smarter',
      description: 'AI-powered crypto trading with pro charts and low fees.',
      image: 'https://1cryptox.com/og-cover.jpg',
    },
  });

  useJsonLD('org', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '1CryptoX',
    url: 'https://1cryptox.com/',
    logo: 'https://1cryptox.com/logo.png',
    sameAs: [
      'https://twitter.com/1cryptox',
      'https://www.instagram.com/1cryptox',
      'https://t.me/onecryptox',
    ],
  });
  useJsonLD('website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '1CryptoX',
    url: 'https://1cryptox.com/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://1cryptox.com/search?q={query}',
      'query-input': 'required name=query',
    },
  });

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box sx={{ width: 280, bgcolor: (theme) => theme.palette.background.paper }}>
      <List>
        {[
          { label: 'Home', href: '/home' },
          { label: 'Markets', href: '/markets' },
          { label: 'About', href: '/about-us' },
          { label: 'Privacy', href: '/privacy' },
        ].map((item) => (
          <ListItem button key={item.label} component="a" href={item.href}>
            <ListItemText
              primary={item.label}
              sx={{ color: (t) => t.palette.text.primary }}
            />
          </ListItem>
        ))}
        {/* زر تحميل التطبيق في الـ SideNav (موبايل) */}
        <ListItem>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            component="a"
            href={APK_DOWNLOAD_URL}
            download
            startIcon={<DownloadIcon sx={{ color: '#1C2526' }} />}
            sx={{
              bgcolor: '#FFC107',
              color: '#1C2526',
              '&:hover': { bgcolor: '#FFCA28' },
            }}
          >
            Download APK
          </Button>
        </ListItem>
        <ListItem>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/register')}
            sx={{ color: '#FFC107', borderColor: 'rgba(240,185,11,0.35)' }}
          >
            Sign Up / Login
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  useEffect(() => {
    const ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setPrices((prev) => {
        const upd = { ...prev };
        data.forEach((t) => {
          if (t.s === 'BTCUSDT') upd.BTCUSDT = parseFloat(t.c).toFixed(2);
          if (t.s === 'ETHUSDT') upd.ETHUSDT = parseFloat(t.c).toFixed(2);
          if (t.s === 'XRPUSDT') upd.XRPUSDT = parseFloat(t.c).toFixed(2);
        });
        return upd;
      });
    };

    const fetchBitcoinChart = async () => {
      const res = await fetch(
        'https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=30'
      );
      const data = await res.json();
      if (chartRef.current) {
        if (chartInstance) chartInstance.destroy();
        const instance = new Chart(chartRef.current, {
          type: 'line',
          data: {
            labels: data.prices.map((p) =>
              new Date(p[0]).toLocaleDateString()
            ),
            datasets: [
              {
                label: 'Bitcoin Price (USD)',
                data: data.prices.map((p) => p[1]),
                borderColor: '#FFC107',
                backgroundColor: 'rgba(255, 193, 7, 0.2)',
                fill: true,
                tension: 0.2,
              },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false },
        });
        setChartInstance(instance);
      }
    };
    fetchBitcoinChart();

    return () => {
      ws.close();
      if (chartInstance) chartInstance.destroy();
      if (profitChartInstance) profitChartInstance.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profitData.weekly]);

  const handleRowClick = (row) => (window.location.href = `/markets/${row.symbol}`);

  // ===== Mobile App Popup (NEW) =====
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [appPopupOpen, setAppPopupOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(
    typeof window !== 'undefined' &&
      localStorage.getItem('hideMobileAppPopup') === 'true'
  );

  useEffect(() => {
    if (isMobile && !dontShowAgain) {
      setAppPopupOpen(true);
    }
  }, [isMobile, dontShowAgain]);

  // 👈 بدلاً من الـ scroll إلى القسم، نجعله يبدأ تحميل الـ APK مباشرة
  const handleGoToDownload = () => {
    if (typeof window !== 'undefined') {
      window.location.href = APK_DOWNLOAD_URL;
    }
    setAppPopupOpen(false);
  };

  const handleClosePopup = () => setAppPopupOpen(false);

  const handleDontShowChange = (e) => {
    const checked = e.target.checked;
    setDontShowAgain(checked);
    if (checked) {
      localStorage.setItem('hideMobileAppPopup', 'true');
    } else {
      localStorage.removeItem('hideMobileAppPopup');
    }
  };
  // ===== End Mobile App Popup =====

  return (
    <ThemeProvider theme={isLightTheme ? lightTheme : darkTheme}>
      <style>{styles}</style>

      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ py: 1, bgcolor: '#1C2526' }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
            {/* BRAND + LOGO (Clickable) */}
            <ButtonBase
              onClick={() => navigate('/home')}
              sx={{ borderRadius: 1, p: 0.5, display: 'inline-flex', alignItems: 'center' }}
              aria-label="Go to home"
            >
              <Box
                component="img"
                src={platformLogo}
                alt="1CryptoX Logo"
                sx={{
                  height: 67,
                  width: 67,
                  objectFit: 'cover',
                  filter: isLightTheme ? 'none' : 'drop-shadow(0 0 2px rgba(240,185,11,0.45))',
                  paddingTop: '4px',
                  mr: -1.3,
                }}
              />
              <Typography
                variant="h6"
                className="text-style"
                sx={{ fontWeight: 'bold', lineHeight: 1 }}
              >
                <span className="hollow">1</span>
                <span className="solid">Crypto</span>
                <span className="hollow">X</span>
              </Typography>
            </ButtonBase>

            {/* Desktop Nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
              <Button
                color="inherit"
                href="/home"
                sx={{ color: '#FFF', '&:hover': { color: '#FFC107' } }}
              >
                Home
              </Button>
              <Button
                color="inherit"
                href="/markets"
                sx={{ color: '#FFF', '&:hover': { color: '#FFC107' } }}
              >
                Markets
              </Button>
              <Button
                color="inherit"
                href="/about-us"
                sx={{ color: '#FFF', '&:hover': { color: '#FFC107' } }}
              >
                About
              </Button>
              <Button
                color="inherit"
                href="/privacy"
                sx={{ color: '#FFF', '&:hover': { color: '#FFC107' } }}
              >
                Privacy
              </Button>

              {/* زر تحميل التطبيق في الـ Desktop Nav */}
              {/* <Button
                variant="outlined"
                component="a"
                href={APK_DOWNLOAD_URL}
                download
                startIcon={<DownloadIcon sx={{ color: '#FFC107' }} />}
                sx={{
                  borderColor: 'rgba(240,185,11,0.6)',
                  color: '#FFC107',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: '#FFC107',
                    bgcolor: 'rgba(240,185,11,0.08)',
                  },
                }}
              >
                Download APK
              </Button> */}

              <Button
                variant="contained"
                sx={{
                  bgcolor: '#FFC107',
                  color: '#1C2526',
                  '&:hover': { bgcolor: '#FFCA28' },
                }}
                onClick={() => navigate('/register')}
              >
                Sign Up
              </Button>
            </Box>

            {/* Mobile Menu */}
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: '#FFF' }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ display: { md: 'none' } }}
      >
        {drawer}
      </Drawer>

      <Box
        sx={{
          bgcolor: '#1C2526',
          minHeight: 'calc(100vh - 64px)',
          p: { xs: 2, md: 4 },
        }}
      >
        {/* SECTION 1: Hero (Visible on load) */}
        <Box
          id="hero"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 6,
          }}
        >
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: { xs: 4, md: 0 } }}>
            <Typography variant="h2" sx={{ color: '#FFF', fontWeight: 'bold' }}>
              Trade Confidently on 1CryptoX
            </Typography>
            <Typography variant="body1" sx={{ color: '#B0BEC5', mt: 2 }}>
              Explore a secure and innovative crypto trading platform designed for
              all levels. Access real-time Bitcoin analysis and AI-based trading
              tools.
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 2,
                bgcolor: '#FFC107',
                color: '#1C2526',
                '&:hover': { bgcolor: '#FFCA28' },
              }}
              onClick={() => navigate('/login')}
            >
              Start Trading
            </Button>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ bgcolor: '#263238', p: 2, borderRadius: 2, width: '100%', maxWidth: 420 }}>
              <Box sx={{ position: 'relative', height: { xs: 180, md: 220 } }}>
                <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* SECTION 2: Features (Visible on load) */}
        <Box id="features" sx={{ mt: 2 }}>
          <FeatureGrid />
        </Box>

        {/* SECTION 3: Partners (Visible on load) */}
        <Box id="partners" sx={{ mt: 4 }}>
          <PartnersMarquee />
        </Box>

        {/* BELOW THE FOLD — تظهر بالتمرير مع Animation */}
        <RevealOnScroll sx={{ mt: 6 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <TrendingCoinsTable onRowClick={handleRowClick} />
            </Box>

            <Card
              sx={{
                flex: 1,
                borderRadius: 2,
                bgcolor: '#263238',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                overflow: 'hidden',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                    color: '#FFFFFF',
                    letterSpacing: 0.2,
                  }}
                >
                  Promotions & Highlights
                </Typography>
                <Typography sx={{ mb: 2, color: '#B0BEC5' }}>
                  Competitive maker/taker fees, referral rewards, VIP tiers, and
                  pro-grade analytics. Instant onboarding and reliable execution
                  for both retail and institutional users.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      label="Low fees"
                      sx={{
                        fontWeight: 700,
                        bgcolor: 'rgba(240,185,11,0.12)',
                        color: '#F0B90B',
                        border: '1px solid rgba(240,185,11,0.25)',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      label="Referral rewards"
                      variant="outlined"
                      sx={{ color: '#EAEFF2', borderColor: 'rgba(255,255,255,0.2)' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      label="TV-like charts"
                      variant="outlined"
                      sx={{ color: '#EAEFF2', borderColor: 'rgba(255,255,255,0.2)' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      label="24/7 Support"
                      variant="outlined"
                      sx={{ color: '#EAEFF2', borderColor: 'rgba(255,255,255,0.2)' }}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
                <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                  Explore more on our{' '}
                  <Button
                    size="small"
                    variant="text"
                    href="/help-center"
                    sx={{ px: 0, minWidth: 0, color: '#F0B90B' }}
                  >
                    Help Center
                  </Button>{' '}
                  and{' '}
                  <Button
                    size="small"
                    variant="text"
                    href="/about-us"
                    sx={{ px: 0, minWidth: 0, color: '#F0B90B' }}
                  >
                    About Us
                  </Button>
                  .
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </RevealOnScroll>

        <RevealOnScroll sx={{ mt: 6 }}>
          <AiTradeCTA />
        </RevealOnScroll>

        <RevealOnScroll sx={{ mt: 6 }}>
          {/* IMPORTANT: added id for smooth scroll target */}
          <Box id="download-app">
            {/* تمرير نفس رابط الـ APK للكومبوننت */}
            <AppDownload imageUrl={mobileImage} downloadHref={APK_DOWNLOAD_URL} />
          </Box>
        </RevealOnScroll>

        <RevealOnScroll sx={{ mt: 6 }}>
          <SocialLinks />
        </RevealOnScroll>

        <RevealOnScroll sx={{ mt: 6 }}>
          <TrustAndSteps />
        </RevealOnScroll>

        <RevealOnScroll sx={{ mt: 6 }}>
          <Footer />
        </RevealOnScroll>
      </Box>

      {/* ===== Mobile App Popup Dialog (NEW) ===== */}
      <Dialog
        open={appPopupOpen}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: '#263238',
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', fontWeight: 800 }}>
          Get the best mobile experience
        </DialogTitle>

        <DialogContent sx={{ color: '#CFD8DC' }}>
          For a smoother and more enjoyable experience on your phone,
          we recommend using our official mobile app.
        </DialogContent>

        <DialogActions
          sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={handleDontShowChange}
                sx={{ color: '#F0B90B' }}
              />
            }
            label="Don’t show this again"
            sx={{ mr: 'auto', color: '#B0BEC5' }}
          />

          <Button
            onClick={handleClosePopup}
            variant="outlined"
            sx={{ color: '#E0E0E0', borderColor: 'rgba(255,255,255,0.25)' }}
          >
            Not now
          </Button>

          <Button
            onClick={handleGoToDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              bgcolor: '#F0B90B',
              color: '#1C2526',
              '&:hover': { bgcolor: '#FFCA28' },
            }}
          >
            Download Now
          </Button>
        </DialogActions>
      </Dialog>
      {/* ===== End Mobile App Popup ===== */}
    </ThemeProvider>
  );
};

export default Welcome;
